import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@heroui/react';

const UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const DISMISSED_SESSION_KEY = 'pwa:update-dismissed';

export const PWAUpdatePrompt = () => {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const [needRefresh, setNeedRefresh] = useState(false);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  const hasReloadedRef = useRef(false);

  // Check if user dismissed the prompt for the current session
  const isDismissedForSession = () => {
    try {
      return sessionStorage.getItem(DISMISSED_SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_SESSION_KEY, 'true');
    } catch {
      // ignore
    }
    setNeedRefresh(false);
  };

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let isDisposed = false;
    const isControlled = () => Boolean(navigator.serviceWorker.controller);

    const triggerRefreshPrompt = () => {
      if (isDisposed) return;
      if (!isDismissedForSession()) {
        setNeedRefresh(true);
      }
    };

    const watchWorker = (worker: ServiceWorker) => {
      worker.addEventListener('statechange', () => {
        if (isDisposed) return;
        if (worker.state === 'installed' && isControlled()) {
          triggerRefreshPrompt();
        }
      });
    };

    const registerServiceWorker = async () => {
      try {
        const nextRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        if (isDisposed) return;

        setRegistration(nextRegistration);

        // If there is an already waiting worker and the page is controlled
        if (nextRegistration.waiting && isControlled()) {
          triggerRefreshPrompt();
        }

        if (nextRegistration.installing) {
          watchWorker(nextRegistration.installing);
        }

        nextRegistration.addEventListener('updatefound', () => {
          const nextWorker = nextRegistration.installing;
          if (nextWorker) {
            watchWorker(nextWorker);
          }
        });

        // Check for updates
        void nextRegistration.update();
      } catch (error) {
        console.warn('[PWA] Service worker registration error:', error);
      }
    };

    const reloadWhenControlled = () => {
      if (hasReloadedRef.current) return;
      hasReloadedRef.current = true;
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', reloadWhenControlled);
    void registerServiceWorker();

    return () => {
      isDisposed = true;
      navigator.serviceWorker.removeEventListener('controllerchange', reloadWhenControlled);
    };
  }, []);

  const checkForUpdate = useCallback(() => {
    if (!registration || !navigator.onLine || isDismissedForSession()) return;
    void registration.update().catch(() => {});
  }, [registration]);

  useEffect(() => {
    if (!registration) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };

    window.addEventListener('focus', checkForUpdate);
    window.addEventListener('online', checkForUpdate);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', checkForUpdate);
      window.removeEventListener('online', checkForUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [checkForUpdate, registration]);

  const applyUpdate = async () => {
    setIsApplyingUpdate(true);

    try {
      sessionStorage.removeItem(DISMISSED_SESSION_KEY);
    } catch {}

    // 1. Tell waiting service worker to skip waiting and take over
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      registration.waiting.postMessage('SKIP_WAITING');
    }

    // 2. Clear stale Cache Storage to prevent stale index.html reload loops
    if ('caches' in window) {
      try {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      } catch (cacheErr) {
        console.warn('[PWA] Failed to purge caches:', cacheErr);
      }
    }

    // 3. Force reload after a short delay if controllerchange doesn't fire
    window.setTimeout(() => {
      if (!hasReloadedRef.current) {
        hasReloadedRef.current = true;
        const url = new URL(window.location.href);
        url.searchParams.set('pwa_update', Date.now().toString());
        window.location.replace(url.toString());
      }
    }, 1200);
  };

  if (!needRefresh) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed inset-x-3 bottom-[calc(5.75rem+max(env(safe-area-inset-bottom),12px))] z-[130] mx-auto max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-2xl shadow-gray-950/20 sm:inset-x-auto sm:right-4 sm:bottom-4 animate-in fade-in slide-in-from-bottom-4 duration-200"
    >
      <div className="flex items-start gap-3">
       

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-950">Update available</h2>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 p-1 -mr-1 -mt-1 rounded-lg transition-colors"
              title="Dismiss"
              aria-label="Dismiss update notification"
            >
              <X size={16} />
            </button>
          </div>
          <p className="mt-1 text-xs leading-5 text-gray-600">
            A new version of the app is available. Update now to get the latest fixes and improvements.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={applyUpdate}
              disabled={isApplyingUpdate}
              className="inline-flex rounded-lg items-center gap-1.5 bg-[#172e50] px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-[#22436f] disabled:cursor-not-allowed disabled:opacity-70 shadow-sm"
            >
              {isApplyingUpdate ? (
                <>
                  Updating...
                </>
              ) : (
                'Update now'
              )}
            </Button>
          
          </div>
        </div>
      </div>
    </div>
  );
};
